/*
    Контент этого файла взят из node_modules/redux. Изменен список параметров, пробрасываемых в редюсеры:
    const nextStateForKey = reducer(previousStateForKey, action, currentState)
    теперь третим параметром передается весь state, также функции типизированы
*/
import { AnyAction, Reducer } from 'redux';

import { Action } from '../../actions';
import { State } from '../../typings/state';

export type Reducers = Record<string, ReducerCustom>;
export type CacheKeysReduxState = Record<string, boolean>;

// tslint:disable-next-line no-any
export type ReducerCustom<S = any, A extends Action = AnyAction, ST = State> = (
    state: S | undefined,
    action: A,
    fullState: ST | undefined
) => S;

export const randomString = (): string =>
    Math.random()
        .toString(36)
        .substring(7)
        .split('')
        .join('.');

export const ActionTypes = {
    INIT: `@@redux/INIT${randomString()}`,
    REPLACE: `@@redux/REPLACE${randomString()}`,
    PROBE_UNKNOWN_ACTION: (): string => `@@redux/PROBE_UNKNOWN_ACTION${randomString()}`
};

export function assertReducerShapeCustom(reducers: Reducers): void {
    Object.keys(reducers).forEach(key => {
        const reducer = reducers[key];
        const initialState = reducer(undefined, { type: ActionTypes.INIT }, undefined);

        if (typeof initialState === 'undefined') {
            throw new Error(
                `Reducer "${key}" returned undefined during initialization. ` +
                    `If the state passed to the reducer is undefined, you must ` +
                    `explicitly return the initial state. The initial state may ` +
                    `not be undefined. If you don't want to set a value for this reducer, ` +
                    `you can use null instead of undefined.`
            );
        }

        if (
            typeof reducer(
                undefined,
                {
                    type: ActionTypes.PROBE_UNKNOWN_ACTION()
                },
                undefined
            ) === 'undefined'
        ) {
            throw new Error(
                `Reducer "${key}" returned undefined when probed with a random type. ` +
                    `Don't try to handle ${ActionTypes.INIT} or other actions in "redux/*" ` +
                    `namespace. They are considered private. Instead, you must return the ` +
                    `current state for any unknown actions, unless it is undefined, ` +
                    `in which case you must return the initial state, regardless of the ` +
                    `action type. The initial state may not be undefined, but can be null.`
            );
        }
    });
}

export function isPlainObject(obj: object | null): boolean {
    if (typeof obj !== 'object' || obj === null) {
        return false;
    }

    let proto = obj;
    while (Object.getPrototypeOf(proto) !== null) {
        proto = Object.getPrototypeOf(proto);
    }

    return Object.getPrototypeOf(obj) === proto;
}

export function getUnexpectedStateShapeWarningMessage(
    inputState: Partial<State>,
    reducers: Reducers,
    action: Action,
    unexpectedKeyCache: CacheKeysReduxState
): string | undefined {
    const reducerKeys = Object.keys(reducers);
    const argumentName =
        action && action.type === ActionTypes.INIT
            ? 'preloadedState argument passed to createStore'
            : 'previous state received by the reducer';

    if (reducerKeys.length === 0) {
        return (
            'Store does not have a valid reducer. Make sure the argument passed ' +
            'to combineReducers is an object whose values are reducers.'
        );
    }

    if (!isPlainObject(inputState)) {
        return (
            `The ${argumentName} has unexpected type of "` +
            {}.toString.call(inputState).match(/\s([a-z|A-Z]+)/)[1] +
            `". Expected argument to be an object with the following ` +
            `keys: "${reducerKeys.join('", "')}"`
        );
    }

    const unexpectedKeys = Object.keys(inputState).filter(
        key => !reducers.hasOwnProperty(key) && !unexpectedKeyCache[key]
    );

    unexpectedKeys.forEach(key => {
        unexpectedKeyCache[key] = true;
    });

    if (action && action.type === ActionTypes.REPLACE) {
        return undefined;
    }

    if (unexpectedKeys.length > 0) {
        return (
            `Unexpected ${unexpectedKeys.length > 1 ? 'keys' : 'key'} ` +
            `"${unexpectedKeys.join('", "')}" found in ${argumentName}. ` +
            `Expected to find one of the known reducer keys instead: ` +
            `"${reducerKeys.join('", "')}". Unexpected keys will be ignored.`
        );
    }

    return undefined;
}

export function getUndefinedStateErrorMessage(key: string, action: Action): string {
    const actionType = action && action.type;
    const actionDescription = (actionType && `action "${String(actionType)}"`) || 'an action';

    return (
        `Given ${actionDescription}, reducer "${key}" returned undefined. ` +
        `To ignore an action, you must explicitly return the previous state. ` +
        `If you want this reducer to hold no value, you can return null instead of undefined.`
    );
}

export function combineReducersCustom(reducers: Reducers): Reducer<State> {
    const reducerKeys = Object.keys(reducers);
    const finalReducers = {};
    for (let i = 0; i < reducerKeys.length; i++) {
        const key = reducerKeys[i];

        if (process.env.NODE_ENV !== 'production') {
            if (typeof reducers[key] === 'undefined') {
                throw new Error(`No reducer provided for key "${key}"`);
            }
        }

        if (typeof reducers[key] === 'function') {
            finalReducers[key] = reducers[key];
        }
    }
    const finalReducerKeys = Object.keys(finalReducers);

    let unexpectedKeyCache: CacheKeysReduxState;
    if (process.env.NODE_ENV !== 'production') {
        unexpectedKeyCache = {};
    }

    let shapeAssertionError: Error;
    try {
        assertReducerShapeCustom(finalReducers);
    } catch (e) {
        shapeAssertionError = e;
    }

    return function combination(state: State | undefined, action: Action): State {
        const currentState = state || {};

        if (shapeAssertionError) {
            throw shapeAssertionError;
        }

        if (process.env.NODE_ENV !== 'production') {
            const warningMessage = getUnexpectedStateShapeWarningMessage(
                currentState,
                finalReducers,
                action,
                unexpectedKeyCache
            );
            if (warningMessage) {
                throw new Error(warningMessage);
            }
        }

        let hasChanged = false;
        const nextState = {};
        for (let i = 0; i < finalReducerKeys.length; i++) {
            const key = finalReducerKeys[i];
            const reducer = finalReducers[key];
            const previousStateForKey = currentState[key];
            const nextStateForKey = reducer(previousStateForKey, action, currentState);
            if (typeof nextStateForKey === 'undefined') {
                const errorMessage = getUndefinedStateErrorMessage(key, action);
                throw new Error(errorMessage);
            }
            nextState[key] = nextStateForKey;
            hasChanged = hasChanged || nextStateForKey !== previousStateForKey;
        }
        return (hasChanged ? nextState : currentState) as State;
    };
}
